import { useEffect, useState } from 'react';

import { OptionType } from '@components/Select';
import { toArrayFromIterable } from '@utils/toArrayFromIterable';
import { isDefined } from '@utils/isDefined';
import { allTeethOption } from '@components/Blocks/RenderedService/RenderedServiceEditForm/selectOptions';
import { dentalMultiSelectServiceTag } from '@services/store/service/constants';
import { serviceService } from '@services/services/serviceService';
import { store } from '@store';

interface Props {
    serviceId: string;
    count: number;
    onCreate(options: OptionType[]): Promise<void>;
    onCreateMulti?(options: OptionType[][]): Promise<void>;
}

interface Data {
    options: Map<string, OptionType>;
    multiOptions: Map<string, OptionType[]>;
    isCreateButtonDisabled: boolean;
    isDentalMulti: boolean;
    onMultiSelect(newOptions: OptionType[], key: string): void;
    onSelect(option: OptionType, key: string): void;
    onCreateClick(): Promise<void>;
}

export function useTeethSelect({ count, serviceId, onCreate, onCreateMulti }: Props): Data {
    const { serviceStore } = store;

    useEffect(() => {
        (async () => {
            await serviceService.loadByIds([serviceId]);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isDentalMulti = !!serviceStore.serviceList.get(serviceId)?.tagIds?.includes(dentalMultiSelectServiceTag);

    const [multiOptions, setMultiOptions] = useState(new Map<string, OptionType[]>());
    const [options, setOptions] = useState(new Map<string, OptionType>());

    const multiOptionsArray = toArrayFromIterable(multiOptions);
    const optionsArray = toArrayFromIterable(options);

    const allOptionsSelected = options.size === count && optionsArray.every(it => isDefined(it.value));
    const allMultiOptionsSelected =
        multiOptions.size === count && multiOptionsArray.every(it => isDefined(it?.length > 0));

    const isCreateButtonDisabled = isDentalMulti ? !allMultiOptionsSelected : !allOptionsSelected;

    const allTeethValue = allTeethOption.value;

    function onMultiSelect(newOptions: OptionType[], key: string) {
        setMultiOptions(currentOptions => {
            const currentSelectedOptions = currentOptions.get(key);
            const hasAllTeethOption = !!currentSelectedOptions?.find(it => it.value === allTeethValue);

            const newAllTeethOption = newOptions?.find(option => option.value === allTeethValue);

            let newStateOptions;

            if (!!newAllTeethOption && !hasAllTeethOption) {
                newStateOptions = new Map(currentOptions.set(key, [newAllTeethOption]));
            } else if (hasAllTeethOption) {
                newStateOptions = new Map(
                    currentOptions.set(
                        key,
                        newOptions.filter(item => item.value !== allTeethValue),
                    ),
                );
            } else {
                newStateOptions = new Map(currentOptions.set(key, newOptions));
            }

            return newStateOptions;
        });
    }

    function onSelect(option: OptionType, key: string) {
        setOptions(currentOptions => new Map(currentOptions.set(key, option)));
    }

    async function onCreateClick(): Promise<void> {
        if (isDentalMulti && !!onCreateMulti) {
            await onCreateMulti(multiOptionsArray);
        } else {
            await onCreate(optionsArray);
        }
    }

    return { onCreateClick, onSelect, onMultiSelect, isDentalMulti, isCreateButtonDisabled, options, multiOptions };
}
